import { useState, useCallback } from 'react';
import { apiService } from '../services/api.ts';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { isAuthSessionExpiredError } from '../services/authService.tsx';

export const useReports = (token) => {
  const { t } = useTranslation();
  const [reports, setReports] = useState([]);
  const [selectedReportId, setSelectedReportId] = useState(null);
  const [selectedReportName, setSelectedReportName] = useState('');
  const [isReportSelected, setIsReportSelected] = useState(false);

  const fetchReports = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await apiService.getReports(token);
      setReports(response);
    } catch (error) {
      if (isAuthSessionExpiredError(error)) return;
      toast.error(t('error_fetching_reports'), { position: "bottom-left" });
    }
  }, [token, t]);

  const createReport = useCallback(async (folders) => {
    if (!token) return;

    const firstFolder = folders?.[0];

    const defaultPayload = {
      data: [],
      customer_id: null,
      name: 'Untitled Report'
    };

    try {
      const response = await apiService.createReport(token, defaultPayload);

      // Use server-assigned folder_id when available; fall back to the first folder passed in
      const assignedFolderId = response.folder_id ?? firstFolder?.folder_id ?? null;

      if (!assignedFolderId) {
        toast.error(t('no_folders_available'), { position: 'bottom-left' });
        return;
      }

      const newReport = {
        report_id: response.report_id,
        name: defaultPayload.name,
        created_at: new Date().toISOString(),
        last_opened_at: null,
        folder_id: assignedFolderId,
        user_id: response.user_id ?? null
      };

      setReports(prev => [...prev, newReport]);
      setSelectedReportId(response.report_id);
      setSelectedReportName(defaultPayload.name);
      setIsReportSelected(true);

      toast.success(t('new_report_created'), { position: 'bottom-left' });
      return newReport;

    } catch (error) {
      if (isAuthSessionExpiredError(error)) return;
      toast.error(t('error_creating_report'), { position: 'bottom-left' });
    }
  }, [token, t]);

  const deleteReport = useCallback(async (reportId) => {
    if (!token) return;
    
    try {
      await apiService.deleteReport(token, reportId);
      setReports(prev => prev.filter(report => report.report_id !== reportId));
      if (selectedReportId === reportId) {
        setSelectedReportId(null);
        setSelectedReportName('');
        setIsReportSelected(false);
      }
      toast.success(t('report_deleted'), { position: "bottom-left" });
    } catch (error) {
      if (isAuthSessionExpiredError(error)) return;
      toast.error(t('error_deleting_report'), { position: "bottom-left" });
    }
  }, [token, selectedReportId, t]);

  const updateReportName = useCallback(async (reportId, newName) => {
    if (!token) return;
    
    try {
      await apiService.updateReport(token, reportId, { name: newName });
      setReports(prev => prev.map(report => 
        report.report_id === reportId ? { ...report, name: newName } : report
      ));
      if (selectedReportId === reportId) {
        setSelectedReportName(newName);
      }
      toast.success(t('report_name_updated'), { position: "bottom-left" });
    } catch (error) {
      if (isAuthSessionExpiredError(error)) return;
      toast.error(t('error_updating_report_name'), { position: "bottom-left" });
    }
  }, [token, selectedReportId, t]);

  const selectReport = useCallback((reportId, reportName) => {
    setSelectedReportId(reportId);
    setSelectedReportName(reportName);
    setIsReportSelected(true);
  }, []);

  const markReportOpened = useCallback(async (reportId) => {
    if (!token || !reportId) return;

    const optimisticOpenedAt = new Date().toISOString();
    setReports(prev => prev.map(report =>
      String(report.report_id) === String(reportId)
        ? { ...report, last_opened_at: optimisticOpenedAt }
        : report
    ));

    try {
      const response = await apiService.markReportOpened(token, reportId);
      const persistedOpenedAt = response?.last_opened_at || optimisticOpenedAt;
      setReports(prev => prev.map(report =>
        String(report.report_id) === String(reportId)
          ? { ...report, last_opened_at: persistedOpenedAt }
          : report
      ));
    } catch (error) {
      console.error('Error recording report open:', error);
    }
  }, [token]);

  return {
    reports,
    setReports,
    selectedReportId,
    setSelectedReportId,
    selectedReportName,
    isReportSelected,
    fetchReports,
    createReport,
    deleteReport,
    updateReportName,
    selectReport,
    markReportOpened,
    setSelectedReportName,
    setIsReportSelected
  };
};
